import { MasteringPreset } from '../types';

export const masteringPresets: MasteringPreset[] = [
  {
    id: 'abbey-road',
    name: 'Abbey Road HD',
    description: 'Classic British console sound with pristine clarity and depth',
    parameters: {
      compression: { 
        threshold: -24, 
        ratio: 2, 
        attack: 10, 
        release: 100, 
        knee: 12, 
        makeupGain: 2 
      },
      saturation: { 
        drive: 1.8, 
        warmth: 0.8, 
        harmonics: 0.4 
      },
      eq: {
        low: { freq: 120, gain: 3, q: 0.8 },
        lowMid: { freq: 400, gain: 1, q: 1.1 },
        mid: { freq: 1800, gain: -1, q: 1.0 },
        highMid: { freq: 4500, gain: 2, q: 1.2 },
        high: { freq: 12000, gain: 2, q: 0.7 }
      },
      stereo: { 
        width: 1.1, 
        monoLowFreq: 200, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -1.0, 
        release: 100 
      }
    }
  },
  {
    id: 'hip-hop-punch',
    name: 'Hip-Hop HD Punch',
    description: 'Ultra-clear drums with deep, controlled bass and pristine highs',
    parameters: {
      compression: { 
        threshold: -16, 
        ratio: 4.5, 
        attack: 3, 
        release: 60, 
        knee: 6, 
        makeupGain: 4 
      },
      saturation: { 
        drive: 2.4, 
        warmth: 0.5, 
        harmonics: 0.6 
      },
      eq: {
        low: { freq: 50, gain: 6, q: 1.2 },
        lowMid: { freq: 200, gain: -2, q: 1.4 },
        mid: { freq: 1000, gain: -1, q: 1.1 },
        highMid: { freq: 3500, gain: 3, q: 1.5 },
        high: { freq: 14000, gain: 4, q: 0.7 }
      },
      stereo: { 
        width: 1.3, 
        monoLowFreq: 120, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -0.2, 
        release: 40 
      }
    }
  },
  {
    id: 'jazz-finesse',
    name: 'Jazz HD Finesse',
    description: 'Pristine clarity with natural dynamics and rich harmonics',
    parameters: {
      compression: { 
        threshold: -30, 
        ratio: 1.5, 
        attack: 25, 
        release: 250, 
        knee: 15, 
        makeupGain: 1 
      },
      saturation: { 
        drive: 1.2, 
        warmth: 0.9, 
        harmonics: 0.2 
      },
      eq: {
        low: { freq: 80, gain: 1.5, q: 0.7 },
        lowMid: { freq: 350, gain: 1, q: 0.9 },
        mid: { freq: 1500, gain: 0.5, q: 1.0 },
        highMid: { freq: 4000, gain: 1, q: 1.1 },
        high: { freq: 8000, gain: 2, q: 0.6 }
      },
      stereo: { 
        width: 1.05, 
        monoLowFreq: 250, 
        dynamicWidth: false 
      },
      limiting: { 
        threshold: -2.5, 
        release: 200 
      }
    }
  },
  {
    id: 'cinematic-epic',
    name: 'Cinematic HD Epic',
    description: 'Immersive surround-like sound with enhanced spatial depth',
    parameters: {
      compression: { 
        threshold: -25, 
        ratio: 2.5, 
        attack: 15, 
        release: 150, 
        knee: 10, 
        makeupGain: 3 
      },
      saturation: { 
        drive: 1.7, 
        warmth: 0.6, 
        harmonics: 0.5 
      },
      eq: {
        low: { freq: 30, gain: 5, q: 0.8 },
        lowMid: { freq: 250, gain: 2, q: 1.2 },
        mid: { freq: 2000, gain: 1, q: 1.3 },
        highMid: { freq: 5000, gain: 3, q: 1.4 },
        high: { freq: 12000, gain: 4, q: 0.6 }
      },
      stereo: { 
        width: 1.5, 
        monoLowFreq: 100, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -0.8, 
        release: 120 
      }
    }
  },
  {
    id: 'lofi-warmth',
    name: 'Lo-Fi HD Warmth',
    description: 'High-fidelity vintage character with pristine detail',
    parameters: {
      compression: { 
        threshold: -22, 
        ratio: 2.2, 
        attack: 12, 
        release: 180, 
        knee: 14, 
        makeupGain: 2 
      },
      saturation: { 
        drive: 2.0, 
        warmth: 0.9, 
        harmonics: 0.4 
      },
      eq: {
        low: { freq: 100, gain: 2.5, q: 0.9 },
        lowMid: { freq: 500, gain: 1.5, q: 1.0 },
        mid: { freq: 1200, gain: -1, q: 1.2 },
        highMid: { freq: 3000, gain: -2, q: 1.3 },
        high: { freq: 7000, gain: -3, q: 0.8 }
      },
      stereo: { 
        width: 0.95, 
        monoLowFreq: 300, 
        dynamicWidth: false 
      },
      limiting: { 
        threshold: -1.5, 
        release: 160 
      }
    }
  },
  {
    id: 'future-bass',
    name: 'Future Bass HD',
    description: 'Crystal-clear modern sound with immersive width',
    parameters: {
      compression: { 
        threshold: -15, 
        ratio: 5, 
        attack: 1, 
        release: 30, 
        knee: 4, 
        makeupGain: 5 
      },
      saturation: { 
        drive: 2.8, 
        warmth: 0.3, 
        harmonics: 0.8 
      },
      eq: {
        low: { freq: 40, gain: 7, q: 1.3 },
        lowMid: { freq: 150, gain: -3, q: 1.5 },
        mid: { freq: 800, gain: -2, q: 1.2 },
        highMid: { freq: 4500, gain: 4, q: 1.7 },
        high: { freq: 16000, gain: 6, q: 0.5 }
      },
      stereo: { 
        width: 1.6, 
        monoLowFreq: 100, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -0.1, 
        release: 20 
      }
    }
  },
  {
    id: 'classical-hall',
    name: 'Classical HD Hall',
    description: 'Concert hall acoustics with pristine detail',
    parameters: {
      compression: { 
        threshold: -32, 
        ratio: 1.3, 
        attack: 30, 
        release: 300, 
        knee: 20, 
        makeupGain: 1 
      },
      saturation: { 
        drive: 1.1, 
        warmth: 0.7, 
        harmonics: 0.1 
      },
      eq: {
        low: { freq: 60, gain: 1, q: 0.7 },
        lowMid: { freq: 300, gain: 0.5, q: 0.8 },
        mid: { freq: 1000, gain: 0, q: 1.0 },
        highMid: { freq: 3000, gain: 1, q: 1.1 },
        high: { freq: 8000, gain: 1.5, q: 0.6 }
      },
      stereo: { 
        width: 1.2, 
        monoLowFreq: 150, 
        dynamicWidth: false 
      },
      limiting: { 
        threshold: -3.0, 
        release: 250 
      }
    }
  },
  {
    id: 'rock-aggression',
    name: 'Rock HD Aggression',
    description: 'High-definition punch with enhanced clarity',
    parameters: {
      compression: { 
        threshold: -18, 
        ratio: 3.5, 
        attack: 8, 
        release: 80, 
        knee: 8, 
        makeupGain: 4 
      },
      saturation: { 
        drive: 2.5, 
        warmth: 0.5, 
        harmonics: 0.7 
      },
      eq: {
        low: { freq: 70, gain: 4, q: 1.1 },
        lowMid: { freq: 400, gain: 3, q: 1.3 },
        mid: { freq: 1500, gain: 2, q: 1.4 },
        highMid: { freq: 4000, gain: 3, q: 1.5 },
        high: { freq: 10000, gain: 3, q: 0.7 }
      },
      stereo: { 
        width: 1.2, 
        monoLowFreq: 180, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -0.5, 
        release: 60 
      }
    }
  },
  {
    id: 'ambient-space',
    name: 'Ambient HD Space',
    description: 'Pristine spatial detail with enhanced depth',
    parameters: {
      compression: { 
        threshold: -28, 
        ratio: 1.8, 
        attack: 20, 
        release: 250, 
        knee: 16, 
        makeupGain: 1.5 
      },
      saturation: { 
        drive: 1.3, 
        warmth: 0.8, 
        harmonics: 0.3 
      },
      eq: {
        low: { freq: 50, gain: 2, q: 0.8 },
        lowMid: { freq: 250, gain: 1, q: 1.0 },
        mid: { freq: 1000, gain: 0, q: 1.1 },
        highMid: { freq: 4000, gain: 2, q: 1.2 },
        high: { freq: 12000, gain: 3, q: 0.6 }
      },
      stereo: { 
        width: 1.4, 
        monoLowFreq: 120, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -2.0, 
        release: 200 
      }
    }
  },
  {
    id: 'vocal-presence',
    name: 'Vocal HD Presence',
    description: 'Crystal-clear vocals with enhanced air and detail',
    parameters: {
      compression: { 
        threshold: -20, 
        ratio: 2.5, 
        attack: 10, 
        release: 120, 
        knee: 10, 
        makeupGain: 2.5 
      },
      saturation: { 
        drive: 1.6, 
        warmth: 0.6, 
        harmonics: 0.4 
      },
      eq: {
        low: { freq: 100, gain: -1, q: 0.9 },
        lowMid: { freq: 300, gain: -2, q: 1.2 },
        mid: { freq: 2000, gain: 3, q: 1.3 },
        highMid: { freq: 5000, gain: 4, q: 1.4 },
        high: { freq: 12000, gain: 2, q: 0.7 }
      },
      stereo: { 
        width: 1.1, 
        monoLowFreq: 200, 
        dynamicWidth: true 
      },
      limiting: { 
        threshold: -1.0, 
        release: 100 
      }
    }
  }
];